import { INotification, MESSAGE_TYPE } from "@home-health-notify/domain";
import { ChatContext, OrganizationContext } from "hhn-commons";
import { useContext, useEffect, useRef, useState } from "react";
import { ReplaceableQueue } from "../utils/ReplaceableQueue";
import { NotificationContext } from "./NotificationContext";

type NotificationProviderType = {
  children: React.JSX.Element;
};

export const NotificationProvider = ({
  children,
}: NotificationProviderType) => {
  const { chat } = useContext(ChatContext);

  const serializedState = localStorage.getItem("_dskN");

  const [isMuted, setIsMuted] = useState<boolean>(
    serializedState ? JSON.parse(serializedState) : false,
  );

  const toggleMute = () => {
    setIsMuted(prev => {
      const newState = !prev;
      localStorage.setItem("_dskN", JSON.stringify(newState));
      return newState;
    });
  };

  const { selectedOrgUser } = useContext(OrganizationContext);

  const notifyUser = async (notification: INotification): Promise<void> => {
    let tonePath;
    switch (notification.messageType) {
      case MESSAGE_TYPE.ACKNOWLEDGE:
        tonePath = "/audio/notification/notification.wav";
        break;
      case MESSAGE_TYPE.PRIORITY:
        tonePath = "/audio/notification/priority.mp3";
        break;
      default:
        tonePath = "/audio/notification/message.mp3";
    }

    const NoOp: (reason: any) => void | PromiseLike<void> = e => {};

    const tone = new Audio(tonePath);
    tone.play().catch(NoOp);
  };

  const notificationQueue = useRef<ReplaceableQueue<INotification>>(
    new ReplaceableQueue<INotification>(notifyUser, 500),
  );

  const notificationHandler = async (
    notification: INotification,
  ): Promise<void> => {
    if (notification.chatId === (chat && chat.getChatId())) {
      return;
    }

    notificationQueue.current.enqueue(
      notification,
      notification => notification.targetId,
      (_new, _existing) => _new.priority < _existing.priority,
    );
  };

  useEffect(() => {
    let unsubscribe: () => void;
    if (selectedOrgUser && !isMuted) {
      unsubscribe = selectedOrgUser
        .getNotificationManager()
        .subscribeNotifications(notificationHandler);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgUser, isMuted, chat]);

  return (
    <NotificationContext.Provider
      value={{
        toggleMute,
        isMuted,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
